import { useMutation } from "@apollo/client";
import { Check, ExternalLink, Link, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gql } from "@/gql";
import { useSalonSlug } from "@/hooks/use-salon-slug";
import { useSalonStore } from "@/lib/store/salon";

// Utility function to generate URL-friendly slug
function generateSlug(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }

  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
}

// Validate slug format
function isValidSlug(slug: string): boolean {
  if (!slug) return false;
  // Must be 3-50 characters, only lowercase letters, numbers, and hyphens
  // Cannot start or end with hyphen
  const slugRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
}

const UPDATE_SALON_MUTATION = gql(`
  mutation UpdateSalon($salonId: String!, $input: UpdateSalonInput!) {
    updateSalon(salonId: $salonId, input: $input) {
      id
      name
      slug
    }
  }
`);

export function SalonSlugConfig() {
  const { t } = useTranslation(["settings", "common"]);
  const salonId = useSalonStore((state) => state.currentSalon?.id);
  const salonName = useSalonStore((state) => state.currentSalon?.name);
  const salonLanguage = useSalonStore((state) => state.currentSalon?.defaultLanguage);

  const [isEditing, setIsEditing] = useState(false);
  const [tempSlug, setTempSlug] = useState("");

  const [updateSalon, { loading: saving }] = useMutation(UPDATE_SALON_MUTATION);

  // Fetch salon slug from database
  const { slug: realSlug, loading: loadingSlug } = useSalonSlug(salonId);

  // Use only real slug from database
  const currentSlug = realSlug || "";

  // Initialize temp slug when editing starts
  useEffect(() => {
    if (isEditing) {
      setTempSlug(currentSlug);
    }
  }, [isEditing, currentSlug]);

  const handleGenerateFromName = useCallback(() => {
    if (salonName) {
      const generatedSlug = generateSlug(salonName);
      setTempSlug(generatedSlug);
    }
  }, [salonName]);

  const handleSaveSlug = useCallback(async () => {
    if (!isValidSlug(tempSlug)) return;
    if (!salonId) return;

    try {
      const { data } = await updateSalon({
        variables: {
          salonId: salonId,
          input: { slug: tempSlug },
        },
      });

      if (data?.updateSalon) {
        toast.success(t("salonSlug.messages.saveSuccess"));
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Failed to update salon slug:", error);
      toast.error(t("salonSlug.messages.saveError"));
    }
  }, [tempSlug, salonId, updateSalon, t]);

  const handleCancelEdit = useCallback(() => {
    setTempSlug(currentSlug);
    setIsEditing(false);
  }, [currentSlug]);

  const storefrontUrl = import.meta.env.VITE_STOREFRONT_URL || "http://localhost:3230";
  const previewUrl = currentSlug ? `${storefrontUrl}/${salonLanguage || "en"}/${currentSlug}` : "";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="mt-1">
          <Link className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-foreground">{t("salonSlug.title")}</h4>
          <p className="text-sm text-muted-foreground mt-1">{t("salonSlug.description")}</p>
        </div>
      </div>

      {/* Slug Configuration */}
      <div className="bg-muted/30 p-4 rounded-lg space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="salon-slug" className="text-sm font-medium">
              {t("salonSlug.fieldLabel")}
            </Label>
            {!isEditing && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={loadingSlug}
              >
                {t("common:actions.edit")}
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    id="salon-slug"
                    value={tempSlug}
                    onChange={(e) => setTempSlug(e.target.value)}
                    placeholder={t("salonSlug.placeholder")}
                    className={`font-mono ${!isValidSlug(tempSlug) && tempSlug ? "border-red-500" : ""}`}
                  />
                  {tempSlug && !isValidSlug(tempSlug) && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <X className="h-3 w-3" />
                      {t("salonSlug.validation.invalid")}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateFromName}
                  disabled={!salonName}
                >
                  {t("salonSlug.generateFromName")}
                </Button>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveSlug}
                  disabled={!isValidSlug(tempSlug) || saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  {saving ? t("common:actions.saving") : t("common:actions.save")}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit}>
                  {t("common:actions.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <code className="px-3 py-2 bg-muted rounded border text-sm font-mono flex-1">
                {loadingSlug ? (
                  <span className="text-muted-foreground italic flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("common:loading")}
                  </span>
                ) : currentSlug ? (
                  currentSlug
                ) : (
                  <span className="text-muted-foreground italic">{t("salonSlug.notSet")}</span>
                )}
              </code>
              {!loadingSlug && currentSlug && isValidSlug(currentSlug) && (
                <Badge variant="secondary" className="text-xs">
                  <Check className="h-3 w-3 mr-1" />
                  {t("salonSlug.valid")}
                </Badge>
              )}
            </div>
          )}
        </div>

        {previewUrl && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("salonSlug.urlPreview")}</Label>
            <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
              <code className="text-sm text-muted-foreground flex-1 break-all">{previewUrl}</code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label={t("urlCopy.openUrl")}
                onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
